import { useAcceptAssignment } from '@/api/callcenter/assignments/acceptAssignment';
import { QueueItem } from '@/api/callcenter/dashboard';
import { toast } from '@/components/ui/elements/use-toast';
import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';

// Screen-level orchestration for opening a queue item from the dashboard.
// QueueCard stays presentational (just an onOpen callback + isPending flag)
// so this claim/navigate/error flow lives in exactly one place instead of
// being duplicated per card.
export const useOpenAssignment = () => {
  const navigate = useNavigate();
  const { mutate } = useAcceptAssignment();

  const [pendingAssignmentId, setPendingAssignmentId] = useState<number | null>(null);
  // Mirrors pendingAssignmentId but is read/written synchronously. Two clicks
  // dispatched in the same tick (e.g. a real double-click) would both still
  // see the pre-click state value, since a React state update isn't visible
  // until the next render -- the ref closes that gap.
  const pendingRef = useRef<number | null>(null);

  const openAssignment = (item: QueueItem) => {
    // A lead currently being worked by the local franchise owner is not
    // ours to claim.
    if (item.externally_held) return;
    // Defensive only -- the server shouldn't hand back a queue item with no
    // lead, but there's nothing sensible to open if it happens.
    if (item.lead == null) return;

    const leadId = String(item.lead.lead_id);

    // Already ours: this agent accepted it earlier (e.g. from a prior visit
    // to the dashboard), so re-posting accept would be redundant.
    if (item.state === 'accepted') {
      navigate({ to: '/leads/lead/$id/quote', params: { id: leadId } });
      return;
    }

    if (pendingRef.current !== null) return;

    pendingRef.current = item.assignment_id;
    setPendingAssignmentId(item.assignment_id);

    mutate(item.assignment_id, {
      onSuccess: () => {
        navigate({ to: '/leads/lead/$id/quote', params: { id: leadId } });
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          const data = error.response?.data as { message?: string } | undefined;
          toast({
            variant: 'destructive',
            title: 'Lead moved',
            description: data?.message ?? 'This lead moved to another agent.',
          });
          return;
        }

        // acceptAssignment's request opts out of the global axios error
        // toast (skipErrorToast), so this is the only error surface for
        // anything that isn't a rotation conflict.
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Could not open this lead. Please try again.',
        });
      },
      onSettled: () => {
        pendingRef.current = null;
        setPendingAssignmentId(null);
      },
    });
  };

  return { openAssignment, pendingAssignmentId };
};
